import {UIGameManager} from '../../UIGameManager'
import {UIGameMapTileEdgeHighlightController} from '../ui_game_map_highlight/UIGameMapTileEdgeHighlightController'
import {GameStateEnums} from '../../../../../../shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIGameActionConfirmationParameters, UIHoverActionController} from '../../ui_game_hover_actions/UIHoverActionController'
import {UIGameMapHighlightParameters, UIGameMapHighlightView} from '../UIGameMapHighlightView'
import {PIXI} from '@colonist/pixi'
import {MapHoverController} from './UIGameMapHoverController'
import {BeginnerHintFormat, GameAction} from '@colonist/game-base-types'
import {ArrayUtils} from '@colonist/utils'

export class UIGameMapTileEdgeHoverController extends UIGameMapTileEdgeHighlightController implements MapHoverController {
    private readonly shouldHighlightOnlyOnHover: boolean
    private readonly hoverMechanicActions: UIHoverActionController[]

    constructor(uiGameManager: UIGameManager, shouldHighlightOnlyOnHover: boolean) {
        super(uiGameManager)
        this.shouldHighlightOnlyOnHover = shouldHighlightOnlyOnHover
        this.hoverMechanicActions = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.GameEnd: this.removeHighlights(); break
            case UIGameEventEnums.BeginnerHintReceived: this.beginnerHintReceived(data); break
            case UIGameEventEnums.GameStateUpdated: this.destroyBeginnerHintView(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            default: break
        }
    }

    registerHoverAction(hoverAction: UIHoverActionController) {
        this.hoverMechanicActions.push(hoverAction)
    }

    onHoverStateChange(isHoverStateActive: boolean) {
        const beginnerHintActive = this.beginnerHintView != undefined
        if(beginnerHintActive) {
            this.destroyBeginnerHintView()
            this.beginnerHintReceived(this.beginnerHint)
        }

        if(!isHoverStateActive) {
            this.removeHighlights()
            return
        }

        const previousRoadEdges = [...this.roadEdges]
        const previousShipEdges = [...this.shipEdges]
        const selectedIndex = this.confirmActionController.selectedLocationIndex

        this.updateRoadAndShipEdges()

        const wasEmpty = this.highlightViews.length === 0
        const hasEdges = this.roadEdges.length > 0 || this.shipEdges.length > 0

        if(wasEmpty && hasEdges) {
            this.updateHighlightsDifferentially([], [], selectedIndex)
        } else if(this.hasEdgeChanges(previousRoadEdges, previousShipEdges)) {
            this.updateHighlightsDifferentially(previousRoadEdges, previousShipEdges, selectedIndex)
        } else if(hasEdges) {
            this.confirmActionController.setupInvisibleBackground(() => {
                this.startAnimation()
                this.hideBeginnerHintInformation()
            })
        }
        this.updateHighlightVisibility()
    }

    onHoverHighlightClick() {
        this.onHoverStateChange(true)
        this.endAnimation()
    }

    onHoverHighlightCancellation() {
        this.startAnimation()
    }

    onClickInvisibleBackground() {
        this.confirmActionController.cleanupConfirmation()
    }

    protected override highlight(highlightParameters: UIGameMapHighlightParameters, action: () => void) {
        const highlightView = new UIGameMapHighlightView(this.uiGameManager, highlightParameters, this.uiGameManager.mapController.mapView, action, false, this.shouldHighlightOnlyOnHover)
        if(this.shouldHighlightOnlyOnHover) {
            highlightView.displayOnlyOnHover()
        } else {
            const canAffordAction = this.getMechanicsAssociatedWithEdge(highlightParameters.index).some((mechanic) => mechanic.canTakeAction)
            highlightView.displayAlwaysOn(canAffordAction)
        }
        this.highlightViews.push(highlightView)
    }

    private updateRoadAndShipEdges() {
        const newRoadEdges: number[] = []
        const newShipEdges: number[] = []

        this.hoverMechanicActions.forEach((mechanicAction: UIHoverActionController) => {
            if(mechanicAction.isCornerAction) return

            if(mechanicAction.isShipAction) newShipEdges.push(...mechanicAction.validLocations)
            else newRoadEdges.push(...mechanicAction.validLocations)
        })

        this.roadEdges = newRoadEdges
        this.shipEdges = newShipEdges
    }

    protected override getActionForEdgeHighlight(highlightParameters: UIGameMapHighlightParameters, index: number): () => void {
        return () => {
            if(this.currentBeginnerModeAction) this.currentBeginnerModeAction.guideText.options = {...this.currentBeginnerModeAction?.guideText.options, context: 'WantToBuildRoad'}
            if(!this.isAllowedPlacementIndex(index)) return this.uiGameManager.eventController.sendEvent(this.currentBeginnerModeAction?.guideText, GameStateEnums.UIGameEventEnums.BeginnerModeForceOpenGuideBubble)

            this.uiGameManager.mapController.hoverController.onClickInvisibleBackground()

            this.destroyBeginnerHintView()
            const buttonSize = highlightParameters.size * 1.75 * this.buttonScaleForMobile
            const highlightCenter = new PIXI.Point(highlightParameters.x + highlightParameters.size / 2, highlightParameters.y + highlightParameters.size / 2)
            const buttonX = highlightCenter.x - buttonSize / 2
            const buttonY = highlightCenter.y - (buttonSize * 1.5)
            this.uiGameManager.mapController.hoverController.onHoverHighlightClick()
            const cancelAction = () => {
                this.uiGameManager.mapController.hoverController.onHoverHighlightCancellation()
            }

            const mechanicsAssociatedWithEdge = this.getMechanicsAssociatedWithEdge(index)
            const confirmationParameters: UIGameActionConfirmationParameters[] = []
            mechanicsAssociatedWithEdge.forEach((mechanic: UIHoverActionController) => confirmationParameters.push(mechanic.getConfirmationParameters(index)))
            this.confirmActionController.showActionConfirmation(highlightParameters, buttonX, buttonY, buttonSize, cancelAction, confirmationParameters, index)
        }
    }

    protected override beginnerHintReceived(data: BeginnerHintFormat): void {
        if(data.type != GameAction.ConfirmBuildRoad) return
        super.beginnerHintReceived(data)
    }

    private getMechanicsAssociatedWithEdge(index: number): UIHoverActionController[] {
        const allowedMechanics: UIHoverActionController[] = []
        for(const mechanic of this.hoverMechanicActions) {
            if(mechanic.isCornerAction) continue
            if(mechanic.validLocations.includes(index)) allowedMechanics.push(mechanic)
        }
        return allowedMechanics
    }

    private updateHighlightVisibility() {
        if(this.shouldHighlightOnlyOnHover) return
        for(const highlightView of this.highlightViews) {
            const canAffordAction = this.getMechanicsAssociatedWithEdge(highlightView.highlightedGameElementIndex).some((mechanic) => mechanic.canTakeAction)
            highlightView.displayAlwaysOn(canAffordAction)
        }
    }

    private hasEdgeChanges(previousRoadEdges: number[], previousShipEdges: number[]): boolean {
        const roadChanges = ArrayUtils.arraysEqual(previousRoadEdges.sort(), this.roadEdges.sort())
        const shipChanges = ArrayUtils.arraysEqual(previousShipEdges.sort(), this.shipEdges.sort())
        const roadChanged = previousRoadEdges.length !== this.roadEdges.length || !roadChanges
        const shipChanged = previousShipEdges.length !== this.shipEdges.length || !shipChanges
        return roadChanged || shipChanged
    }
}
